import dayjs from 'dayjs';
/* eslint-disable no-nested-ternary */
import pkg from 'package.json';

import constant from '@/renderer/scripts/utils/ConstantConfig';
import store from '@/renderer/store';
import user from '@/renderer/scripts/utils/user';
import {
  IS_DEV,
  IS_TEST,
  IS_PRIVATE,
  IS_BROWSER,
  IS_PC,
  IS_PC_IN_WIN,
  IS_PC_IN_MAC,
  IS_PC_IN_ARM_LINUX,
  PKG_PLATFORM,
} from '@/common/constant/platform';
import { HttpUrl } from '@/renderer/scripts/http';
import { getDeviceName } from '@/renderer/scripts/utils/tools';

import { USER_ROLE_MAP, getCodeAndParams } from './utils';

// 开关控制可选是否上报，除了本地启动和私有化外所有环境都上报。本地开发时只打印，不上报
const currentEnv = HttpUrl.getCurrentEnv();
const reporterIsForbidden = IS_PRIVATE || currentEnv === '';
const deviceName = getDeviceName();
const showLog = (IS_DEV || IS_TEST) && !IS_PRIVATE;

const isSameDay = (timeStamp: number) => {
  if (!timeStamp) return false;
  const date = dayjs(timeStamp);
  const todayDate = dayjs();
  const year = date.year();
  const month = date.month() + 1;
  const day = date.date();
  const thisYear = todayDate.year();
  const thisMonth = todayDate.month() + 1;
  const thisDay = todayDate.date();
  return year === thisYear && month === thisMonth && day === thisDay;
};

class ReporterClass {
  private ins: any;

  constructor() {
    this.init();
  }

  public init() {
    if (reporterIsForbidden) return;

    const osVersion = IS_PC_IN_WIN ? 'win' : IS_PC_IN_MAC ? 'mac' : IS_PC_IN_ARM_LINUX ? 'linux' : 'unknown';
    const deviceName = getDeviceName();

    // appkey获取在灯塔datahub  http://datahub.tencent.com/storage_product/data_link
    const appkey = IS_BROWSER ? '0WEB0UV5FW44OLKY' : '0WEB0EW5FW4GQBDU';
    const BeaconAction = require('@tencent/beacon-web-sdk');
    this.ins = new BeaconAction({
      appkey, // 系统或项目id, 必填
      versionCode: pkg.version, // 项目版本,选填
      channelID: osVersion, // 渠道,pc端上送为操作系统类型
      openid: deviceName, // 用户id, 选填
      unionid: '', // 用户unionid , 类似idfv,选填
      strictMode: false, // 严苛模式开关, 打开严苛模式会主动抛出异常, 上线请务必关闭!!!
      delay: 5000, // 普通事件延迟上报时间(单位毫秒), 默认1000(1秒),选填
      sessionDuration: 60 * 1000, // session变更的时间间隔, 一个用户持续30分钟(默认值)没有任何上报则算另一次 session,每变更一次session上报一次启动事件(rqd_applaunched),使用毫秒(ms),最小值30秒,选填
    });
  }

  public report(eventCode: string, params?: any) {
    // 获取真实上报的code和params
    const { reportCode, reportParams } = getCodeAndParams(eventCode, params);

    if (!reportCode || typeof reportCode !== 'string') return;

    if (!this.ins) {
      // 兜底初始化
      this.init();
    }

    const { systemUsedInfo } = store.getters;
    const { isLogined } = user.getUserInfo();

    // 补充渠道和浏览器类型参数
    const fullParams = reportParams ? { ...reportParams } : {};
    Object.assign(fullParams, {
      source_channel: constant.sourceChannel,
      browser_type: deviceName,
      env: currentEnv,
    });

    if (IS_PC && PKG_PLATFORM && PKG_PLATFORM !== 'null') {
      Object.assign(fullParams, { pkg_platform: PKG_PLATFORM });
    }

    // 登录验证时触发，渠道新用户会自动创建体验版,算为新入用户上报
    if (eventCode === 'tced_new') {
      const { isNewUser, userId } = user.getUserInfo();
      Object.assign(fullParams, {
        is_new_user: isNewUser,
        user_id: `${userId}`,
      });
    }

    if (isLogined) {
      // 登录后的事件都上报用户id、是否新用户、用户角色、所属企业id
      const { isNewUser, isOrgNewUser, userId, userRole, organizationId, orgChannel = 'base' } = user.getUserInfo();
      Object.assign(fullParams, {
        user_id: `${userId}`,
        is_new_user: isNewUser,
        is_org_new_user: isOrgNewUser,
        user_charaterator_id: USER_ROLE_MAP[userRole as string] || userRole || 'unknown',
        org_id: `${organizationId}`,
        channel_source: orgChannel || 'base',
      });
    }

    showLog && console.log('[reporter]', reportCode, fullParams);

    // 使用灯塔的非实时上报，按delay周期性上报
    this.ins && this.ins.onUserAction(reportCode, fullParams);

    // 任意操作后统计为使用过，用于灯塔里快速统计当日使用网盘的人数
    const { systemUsed, systemUsedTS } = systemUsedInfo;
    // 判断是否今天没有上报过usedAfterLogined事件
    const didNotReportSystemUsedToday = !systemUsed || !isSameDay(systemUsedTS);
    if (didNotReportSystemUsedToday) {
      store.commit('setSystemUsed', {
        systemUsed: true,
        systemUsedTS: dayjs().valueOf(),
      });
      this.ins && this.ins.onUserAction('system_used', fullParams);
      showLog && console.log('[reporter]', 'system_used', fullParams);
    }

    if (isLogined) {
      // 登录后的事件每天至少记录一次 usedAfterLogined；严谨到获取到用户身份才上报
      const { userRole } = user.getUserInfo();
      if (!userRole || userRole === 'unknown') return;
      const { usedAfterLogined, afterLoginedTS } = systemUsedInfo;
      // 判断是否今天没有上报过usedAfterLogined事件
      const didNotReportToday = !usedAfterLogined || !isSameDay(afterLoginedTS);
      if (didNotReportToday) {
        store.commit('setSystemUsed', {
          usedAfterLogined: true,
          afterLoginedTS: dayjs().valueOf(),
        });
        this.ins && this.ins.onUserAction('used_after_logined', fullParams);
        showLog && console.log('[reporter]', 'used_after_logined', fullParams);
      }
    } else {
      // 登录前的事件每天至少记录一次 usedBeforeLogined
      const { usedBeforeLogined, beforeLoginedTS } = systemUsedInfo;
      // 判断是否今天没有上报过usedBeforeLogined事件
      const didNotReportToday = !usedBeforeLogined || !isSameDay(beforeLoginedTS);
      if (didNotReportToday) {
        store.commit('setSystemUsed', {
          usedBeforeLogined: true,
          beforeLoginedTS: dayjs().valueOf(),
        });
        this.ins && this.ins.onUserAction('used_before_logined', fullParams);
        showLog && console.log('[reporter]', 'used_before_logined', fullParams);
      }
    }
  }
}

const reporter: Reporter = new ReporterClass();

window.reporter = reporter;
export default reporter;
